import type { RugbyMatchResultTeamSummary } from "@dugads/shared";

/**
 * 084c -- International rugby-broadcast style scoreboard:
 *   [HOME] [score] - [score] [AWAY]
 *
 * 085 -- Chips now render the school crest + primary/secondary brand colours
 * when available; unbranded schools fall back to neutral theme tokens.
 */

interface Props {
  home: RugbyMatchResultTeamSummary | null;
  away: RugbyMatchResultTeamSummary | null;
  homeScore: number;
  awayScore: number;
  compact?: boolean;
  /** Highlight the side that just scored with a coloured pulse behind the chip. */
  flashSide?: "home" | "away" | null;
  /** Event type driving the flash colour (try, conversion, penalty, etc.). */
  flashEventType?: string | null;
}

export function MatchScoreboard({ home, away, homeScore, awayScore, compact = false, flashSide = null, flashEventType = null }: Props) {
  const chipText = compact ? "text-[10px]" : "text-xs";
  const chipPad = compact ? "px-1.5 py-0.5" : "px-3 py-1.5";
  const scoreText = compact ? "text-lg" : "text-4xl";
  const sepText = compact ? "text-xs" : "text-2xl";
  const gap = compact ? "gap-0" : "gap-1";

  return (
    <div className={`inline-flex items-stretch overflow-hidden rounded border border-border bg-card ${gap}`} data-field="match-scoreboard">
      <Chip
        team={home}
        fallbackLabel="HOME"
        tone="home"
        flash={flashSide === "home"}
        flashEventType={flashEventType}
        chipText={chipText}
        chipPad={chipPad}
        compact={compact}
      />
      <ScoreCell value={homeScore} tone="home" className={scoreText} compact={compact} />
      <div className={`flex items-center bg-muted text-muted-foreground ${sepText} font-semibold ${compact ? "px-1" : "px-2"}`} aria-hidden>
        -
      </div>
      <ScoreCell value={awayScore} tone="away" className={scoreText} compact={compact} />
      <Chip
        team={away}
        fallbackLabel="AWAY"
        tone="away"
        flash={flashSide === "away"}
        flashEventType={flashEventType}
        chipText={chipText}
        chipPad={chipPad}
        compact={compact}
      />
    </div>
  );
}

function Chip({
  team,
  fallbackLabel,
  tone,
  flash,
  flashEventType,
  chipText,
  chipPad,
  compact,
}: {
  team: RugbyMatchResultTeamSummary | null;
  fallbackLabel: string;
  tone: "home" | "away";
  flash: boolean;
  flashEventType?: string | null;
  chipText: string;
  chipPad: string;
  compact: boolean;
}) {
  const label = team?.school_abbreviation ?? team?.team_display_name ?? fallbackLabel;
  const branded = !!(team?.school_primary_color && team?.school_secondary_color);

  const baseClass = !branded
    ? (tone === "home" ? "bg-foreground text-background" : "bg-muted-foreground text-background")
    : "";
  const flashClasses = flash ? `animate-pulse ${eventFlashClass(flashEventType ?? null)}` : "";

  const brandedStyle = branded
    ? {
        backgroundColor: team!.school_primary_color!,
        color: team!.school_secondary_color!,
      }
    : undefined;

  const logoSize = compact ? "h-3.5 w-3.5" : "h-5 w-5";

  return (
    <div
      className={`relative flex items-center gap-1.5 font-bold uppercase tracking-wide ${chipText} ${chipPad} ${baseClass} ${flashClasses}`}
      data-side={tone}
      data-branded={branded ? "true" : "false"}
      style={brandedStyle}
    >
      {team?.school_logo_url && (
        <img
          src={team.school_logo_url}
          alt=""
          className={`${logoSize} object-contain`}
          data-field="school-logo"
        />
      )}
      <span>{truncate(label, 6)}</span>
    </div>
  );
}

function eventFlashClass(eventType: string | null): string {
  switch (eventType) {
    case "try": return "bg-green-600 text-white";
    case "conversion": return "bg-sky-500 text-white";
    case "penalty": return "bg-orange-500 text-white";
    case "drop_goal": return "bg-violet-500 text-white";
    case "yellow_card": return "bg-amber-400 text-amber-900";
    case "red_card": return "bg-red-600 text-white";
    default: return "bg-amber-400 text-amber-900";
  }
}

function ScoreCell({
  value,
  tone,
  className,
  compact,
}: {
  value: number;
  tone: "home" | "away";
  className: string;
  compact: boolean;
}) {
  return (
    <div
      className={`flex min-w-[2.5ch] items-center justify-center font-bold tabular-nums ${className} ${compact ? "px-1.5" : "px-3"}`}
      data-side={tone}
      data-field={`score-${tone}`}
    >
      {value}
    </div>
  );
}

function truncate(s: string, max: number): string {
  if (s.length <= max) return s;
  return s.slice(0, max).toUpperCase();
}
